import { useMemo } from 'react';

export const useDuplicateWarnings = ({
  form,
  currentLimits,
  devices,
  zones,
  deviceTypes,
}: {
  form: any;
  currentLimits: any[];
  devices: any[];
  zones: any[];
  deviceTypes: any[];
}) => {
  return useMemo(() => {
    const warnings: string[] = [];

    if (!currentLimits || currentLimits.length === 0) {
      return warnings;
    }

    const formValues = form.values;

    // Helper function to check if zones overlap
    const zonesOverlap = (formZones: string[], existingZones: string[]) => {
      // If either has no zones selected, it means "all zones" - so they always overlap
      if (formZones.length === 0 || existingZones.length === 0) {
        return true;
      }
      // Otherwise check for actual zone intersection
      return formZones.some(zId => existingZones.includes(zId));
    };

    // Helper function to get device zone IDs for a device
    const getDeviceZoneIds = (deviceUuid: string) => {
      const device = devices.find(d => d.uuid === deviceUuid);
      return device?.zoneIds || device?.zones?.map((z: any) => z.id) || [];
    };

    // Helper function to check if device is in any of the specified zones
    const isDeviceInZones = (deviceUuid: string, zoneIds: string[]) => {
      if (zoneIds.length === 0) return true; // "all zones"
      const deviceZoneIds = getDeviceZoneIds(deviceUuid);
      return deviceZoneIds.some((dzId: string) => zoneIds.includes(dzId));
    };

    // Check for duplicates based on limit type
    currentLimits.forEach((existingLimit: any) => {
      // 1. DEVICES type duplicates (same as before)
      if (formValues.type === 'DEVICES' && existingLimit.type === 'DEVICES') {
        const formDevices = formValues.devices || [];
        const existingDevices = existingLimit.devices?.map((d: any) => d.uuid) || [];

        const overlappingDevices = formDevices.filter((deviceUuid: string) =>
          existingDevices.includes(deviceUuid),
        );

        if (overlappingDevices.length > 0) {
          const deviceNames = overlappingDevices
            .map((uuid: string) => devices.find(d => d.uuid === uuid)?.uniqueId)
            .filter(Boolean)
            .join(', ');
          warnings.push(`Limit "${existingLimit.name}" już istnieje dla urządzeń: ${deviceNames}`);
        }
      }

      // 2. DEVICE_TYPE duplicates with enhanced zone overlap detection
      if (formValues.type === 'DEVICE_TYPE' && existingLimit.type === 'DEVICE_TYPE') {
        const formDeviceTypes = formValues.deviceType || [];
        const formZones = formValues.zone || [];
        const existingDeviceTypes = existingLimit.deviceType?.map((dt: any) => dt.id) || [];
        const existingZones = existingLimit.zone?.map((z: any) => z.id) || [];

        const overlappingDeviceTypes = formDeviceTypes.filter((dtId: string) =>
          existingDeviceTypes.includes(dtId),
        );

        if (overlappingDeviceTypes.length > 0 && zonesOverlap(formZones, existingZones)) {
          const deviceTypeNames = overlappingDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          let zoneInfo = '';
          if (formZones.length === 0 && existingZones.length === 0) {
            zoneInfo = 'wszystkie strefy';
          } else if (formZones.length === 0) {
            zoneInfo = 'wszystkie strefy (w tym już istniejące limity dla konkretnych stref)';
          } else if (existingZones.length === 0) {
            zoneInfo = 'wszystkie strefy (już istniejący limit dla wszystkich stref)';
          } else {
            const overlappingZoneNames = zones
              .filter((z: any) => formZones.includes(z.id) && existingZones.includes(z.id))
              .map((z: any) => z.name)
              .join(', ');
            zoneInfo = overlappingZoneNames;
          }

          warnings.push(
            `Limit "${existingLimit.name}" już istnieje dla typu urządzenia: ${deviceTypeNames} w ${zoneInfo}`,
          );
        }
      }

      // 3. DEVICE_TYPE_ALL_DEVICES duplicates with enhanced zone overlap detection
      if (
        formValues.type === 'DEVICE_TYPE_ALL_DEVICES' &&
        existingLimit.type === 'DEVICE_TYPE_ALL_DEVICES'
      ) {
        const formDeviceTypes = formValues.deviceType || [];
        const formZones = formValues.zone || [];
        const existingDeviceTypes = existingLimit.deviceType?.map((dt: any) => dt.id) || [];
        const existingZones = existingLimit.zone?.map((z: any) => z.id) || [];

        const overlappingDeviceTypes = formDeviceTypes.filter((dtId: string) =>
          existingDeviceTypes.includes(dtId),
        );

        if (overlappingDeviceTypes.length > 0 && zonesOverlap(formZones, existingZones)) {
          const deviceTypeNames = overlappingDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          let zoneInfo = '';
          if (formZones.length === 0 && existingZones.length === 0) {
            zoneInfo = 'wszystkie strefy';
          } else if (formZones.length === 0) {
            zoneInfo = 'wszystkie strefy (w tym już istniejące limity dla konkretnych stref)';
          } else if (existingZones.length === 0) {
            zoneInfo = 'wszystkie strefy (już istniejący limit dla wszystkich stref)';
          } else {
            const overlappingZoneNames = zones
              .filter((z: any) => formZones.includes(z.id) && existingZones.includes(z.id))
              .map((z: any) => z.name)
              .join(', ');
            zoneInfo = overlappingZoneNames;
          }

          warnings.push(
            `Limit "${existingLimit.name}" już istnieje dla typu urządzenia (indywidualne urządzenia): ${deviceTypeNames} w ${zoneInfo}`,
          );
        }
      }

      // 4. Cross-type detection: DEVICES vs DEVICE_TYPE
      if (formValues.type === 'DEVICES' && existingLimit.type === 'DEVICE_TYPE') {
        const formDevices = formValues.devices || [];
        const existingDeviceTypes = existingLimit.deviceType?.map((dt: any) => dt.id) || [];
        const existingZones = existingLimit.zone?.map((z: any) => z.id) || [];

        // Check if any selected device belongs to the existing device type and is in the relevant zones
        const conflictingDevices = formDevices.filter((deviceUuid: string) => {
          const device = devices.find(d => d.uuid === deviceUuid);
          const deviceTypeMatches = device && existingDeviceTypes.includes(device.deviceTypeId);
          const deviceInZones = isDeviceInZones(deviceUuid, existingZones);
          return deviceTypeMatches && deviceInZones;
        });

        if (conflictingDevices.length > 0) {
          const conflictingDeviceNames = conflictingDevices
            .map((uuid: string) => devices.find(d => d.uuid === uuid)?.uniqueId)
            .filter(Boolean)
            .join(', ');

          const deviceTypeNames = existingDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          const zoneInfo =
            existingZones.length === 0
              ? 'wszystkie strefy'
              : zones
                  .filter((z: any) => existingZones.includes(z.id))
                  .map((z: any) => z.name)
                  .join(', ');

          warnings.push(
            `Urządzenia ${conflictingDeviceNames} są już objęte limitem "${existingLimit.name}" dla typu urządzenia: ${deviceTypeNames} w ${zoneInfo}`,
          );
        }
      }

      // 5. Cross-type detection: DEVICE_TYPE vs DEVICES
      if (formValues.type === 'DEVICE_TYPE' && existingLimit.type === 'DEVICES') {
        const formDeviceTypes = formValues.deviceType || [];
        const formZones = formValues.zone || [];
        const existingDevices = existingLimit.devices?.map((d: any) => d.uuid) || [];

        // Check if any existing device belongs to the form's device type and is in the relevant zones
        const conflictingDevices = existingDevices.filter((deviceUuid: string) => {
          const device = devices.find(d => d.uuid === deviceUuid);
          const deviceTypeMatches = device && formDeviceTypes.includes(device.deviceTypeId);
          const deviceInZones = isDeviceInZones(deviceUuid, formZones);
          return deviceTypeMatches && deviceInZones;
        });

        if (conflictingDevices.length > 0) {
          const conflictingDeviceNames = conflictingDevices
            .map((uuid: string) => devices.find(d => d.uuid === uuid)?.uniqueId)
            .filter(Boolean)
            .join(', ');

          const deviceTypeNames = formDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          const zoneInfo =
            formZones.length === 0
              ? 'wszystkie strefy'
              : zones
                  .filter((z: any) => formZones.includes(z.id))
                  .map((z: any) => z.name)
                  .join(', ');

          warnings.push(
            `Typ urządzenia ${deviceTypeNames} w ${zoneInfo} będzie konflikował z istniejącym limitem "${existingLimit.name}" dla urządzeń: ${conflictingDeviceNames}`,
          );
        }
      }

      // 6. Cross-type detection: DEVICES vs DEVICE_TYPE_ALL_DEVICES
      if (formValues.type === 'DEVICES' && existingLimit.type === 'DEVICE_TYPE_ALL_DEVICES') {
        const formDevices = formValues.devices || [];
        const existingDeviceTypes = existingLimit.deviceType?.map((dt: any) => dt.id) || [];
        const existingZones = existingLimit.zone?.map((z: any) => z.id) || [];

        const conflictingDevices = formDevices.filter((deviceUuid: string) => {
          const device = devices.find(d => d.uuid === deviceUuid);
          const deviceTypeMatches = device && existingDeviceTypes.includes(device.deviceTypeId);
          const deviceInZones = isDeviceInZones(deviceUuid, existingZones);
          return deviceTypeMatches && deviceInZones;
        });

        if (conflictingDevices.length > 0) {
          const conflictingDeviceNames = conflictingDevices
            .map((uuid: string) => devices.find(d => d.uuid === uuid)?.uniqueId)
            .filter(Boolean)
            .join(', ');

          const deviceTypeNames = existingDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          const zoneInfo =
            existingZones.length === 0
              ? 'wszystkie strefy'
              : zones
                  .filter((z: any) => existingZones.includes(z.id))
                  .map((z: any) => z.name)
                  .join(', ');

          warnings.push(
            `Urządzenia ${conflictingDeviceNames} są już objęte limitem "${existingLimit.name}" dla typu urządzenia (indywidualne urządzenia): ${deviceTypeNames} w ${zoneInfo}`,
          );
        }
      }

      // 7. Cross-type detection: DEVICE_TYPE_ALL_DEVICES vs DEVICES
      if (formValues.type === 'DEVICE_TYPE_ALL_DEVICES' && existingLimit.type === 'DEVICES') {
        const formDeviceTypes = formValues.deviceType || [];
        const formZones = formValues.zone || [];
        const existingDevices = existingLimit.devices?.map((d: any) => d.uuid) || [];

        const conflictingDevices = existingDevices.filter((deviceUuid: string) => {
          const device = devices.find(d => d.uuid === deviceUuid);
          const deviceTypeMatches = device && formDeviceTypes.includes(device.deviceTypeId);
          const deviceInZones = isDeviceInZones(deviceUuid, formZones);
          return deviceTypeMatches && deviceInZones;
        });

        if (conflictingDevices.length > 0) {
          const conflictingDeviceNames = conflictingDevices
            .map((uuid: string) => devices.find(d => d.uuid === uuid)?.uniqueId)
            .filter(Boolean)
            .join(', ');

          const deviceTypeNames = formDeviceTypes
            .map((dtId: string) => deviceTypes.find(dt => dt.id === dtId)?.name)
            .filter(Boolean)
            .join(', ');

          const zoneInfo =
            formZones.length === 0
              ? 'wszystkie strefy'
              : zones
                  .filter((z: any) => formZones.includes(z.id))
                  .map((z: any) => z.name)
                  .join(', ');

          warnings.push(
            `Typ urządzenia (indywidualne urządzenia) ${deviceTypeNames} w ${zoneInfo} będzie konflikował z istniejącym limitem "${existingLimit.name}" dla urządzeń: ${conflictingDeviceNames}`,
          );
        }
      }
    });

    return warnings;
  }, [form.values, currentLimits, devices, zones, deviceTypes]);
};
